// MARK://dhk8/pos/features/kds/StationPicker
// Pick which kitchen station this screen serves (or "all"). The choice is persisted
// in sessionStorage by kds-store. Touch-sized (≥44pt) for tablet portrait.
// Absorbed from `apps/dhk8-kds/src/pages/StationPickerPage.tsx`. When a
// dhk8-kds-stations contract is published this becomes a real list; for now it's a
// free-text id + "todas". TODO backend: dhk8-kds-stations list.

import { useState } from 'react'
import { ChefHat } from 'lucide-react'
import { useKdsStore } from './kds-store.js'
import { t } from '../../i18n/es-MX.js'

export default function StationPicker() {
  const setStation = useKdsStore((s) => s.setStation)
  const [id, setId] = useState('')
  const trimmed = id.trim()

  return (
    <div className="dhk8-kds-picker" data-density="compact">
      <div className="dhk8-kds-picker__card">
        <div className="dhk8-kds-picker__brand"><span className="dhk8-kds-picker__glyph"><ChefHat size={20} /></span><span>{t('kds.title')}</span></div>
        <h1>{t('kds.pickStationTitle')}</h1>
        <p>{t('kds.pickStationHint')}</p>
        <div className="dhk8-kds-picker__field">
          <label htmlFor="dhk8-kds-station">{t('kds.stationIdLabel')}</label>
          <input
            id="dhk8-kds-station"
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder={t('kds.stationIdPlaceholder')}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter' && trimmed) setStation(trimmed) }}
          />
        </div>
        <div className="dhk8-kds-picker__actions">
          <button type="button" className="dhk8-kds-picker__btn dhk8-kds-picker__btn--primary" disabled={!trimmed} onClick={() => setStation(trimmed)}>
            {t('kds.useThisStation')}
          </button>
          <button type="button" className="dhk8-kds-picker__btn" onClick={() => setStation(null)}>
            {t('kds.allStations')}
          </button>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  )
}

// HIG-green station picker. Rounded card, soft shadow, hairline; green-filled
// primary CTA + tinted secondary. Theme-aware `--pos-*` tokens only.
const styles = `
  .dhk8-kds-picker { min-height:100%; display:flex; align-items:center; justify-content:center; padding:var(--pos-space-5); }
  .dhk8-kds-picker__card {
    max-width:420px; width:100%; padding:var(--pos-space-6);
    border:1px solid var(--pos-separator); border-radius:var(--pos-radius-xl);
    background:var(--pos-surface); box-shadow:var(--pos-shadow-3);
    display:flex; flex-direction:column; gap:var(--pos-space-3);
  }
  .dhk8-kds-picker__brand { display:flex; align-items:center; gap:var(--pos-space-2); font-family:var(--font-display, var(--font-sans)); font-size:var(--pos-text-lg); font-weight:var(--pos-weight-semibold); color:var(--pos-fg); }
  .dhk8-kds-picker__glyph {
    width:32px; height:32px; display:inline-flex; align-items:center; justify-content:center;
    border-radius:var(--pos-radius-md); color:#fff;
    background:linear-gradient(160deg, var(--hig-green-500), var(--hig-green-700));
    box-shadow:var(--pos-shadow-emboss);
  }
  .dhk8-kds-picker__card h1 { margin:var(--pos-space-2) 0 0; font-family:var(--font-display, var(--font-sans)); font-size:var(--pos-text-xl); font-weight:var(--pos-weight-semibold); letter-spacing:var(--pos-tracking-tight); color:var(--pos-fg); }
  .dhk8-kds-picker__card p { margin:0; font-family:var(--font-sans); font-size:var(--pos-text-sm); color:var(--pos-fg-secondary); line-height:var(--pos-leading-normal); }
  .dhk8-kds-picker__field { display:flex; flex-direction:column; gap:var(--pos-space-1); margin-top:var(--pos-space-2); }
  .dhk8-kds-picker__field label { font-family:var(--font-sans); font-size:var(--pos-text-xxs); font-weight:var(--pos-weight-medium); letter-spacing:var(--pos-tracking-wide); text-transform:uppercase; color:var(--pos-fg-tertiary); }
  .dhk8-kds-picker__field input {
    min-height:44px; padding:var(--pos-space-2) var(--pos-space-3);
    border:1px solid var(--pos-separator-strong); border-radius:var(--pos-radius-md);
    background:var(--pos-elevated); color:var(--pos-fg);
    font-family:var(--font-sans); font-size:var(--pos-text-md); outline:none;
    transition:border-color var(--pos-duration-fast) var(--pos-ease-out), box-shadow var(--pos-duration-fast) var(--pos-ease-out);
  }
  .dhk8-kds-picker__field input::placeholder { color:var(--pos-fg-tertiary); }
  .dhk8-kds-picker__field input:focus { border-color:var(--pos-tint); box-shadow:0 0 0 3px var(--pos-tint-soft); }
  .dhk8-kds-picker__actions { display:flex; flex-direction:column; gap:var(--pos-space-2); margin-top:var(--pos-space-3); }
  .dhk8-kds-picker__btn {
    min-height:44px; padding:var(--pos-space-3); border:1px solid var(--pos-separator-strong);
    border-radius:var(--pos-radius-md); background:var(--pos-elevated); color:var(--pos-fg);
    font-family:var(--font-sans); font-size:var(--pos-text-md); font-weight:var(--pos-weight-medium); cursor:pointer;
    transition:background var(--pos-duration-fast) var(--pos-ease-out);
  }
  .dhk8-kds-picker__btn:hover:not(:disabled) { background:var(--pos-tint-soft); }
  .dhk8-kds-picker__btn:active:not(:disabled) { background:var(--pos-tint-hover); }
  .dhk8-kds-picker__btn:focus-visible { outline:2px solid var(--pos-tint); outline-offset:2px; }
  .dhk8-kds-picker__btn--primary {
    background:var(--pos-tint); color:#fff; border-color:transparent;
    font-weight:var(--pos-weight-semibold); box-shadow:var(--pos-shadow-1);
  }
  .dhk8-kds-picker__btn--primary:hover:not(:disabled) { background:var(--pos-tint-strong); }
  .dhk8-kds-picker__btn--primary:active:not(:disabled) { background:var(--pos-tint-strong); }
  .dhk8-kds-picker__btn:disabled { opacity:.4; cursor:not-allowed; }
`
